import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { MessageCircle, Send, AlertTriangle } from "lucide-react";

function Stat({ icon: Icon, label, value, tone = "slate", testid }) {
  const tones = {
    slate: "bg-slate-800 border-slate-700 text-slate-200",
    emerald: "bg-emerald-500/10 border-emerald-500/30 text-emerald-300",
    red: "bg-red-500/10 border-red-500/30 text-red-300",
    sky: "bg-sky-500/10 border-sky-500/30 text-sky-300",
  };
  return (
    <div data-testid={testid} className="bg-[#151A23] border border-slate-800 rounded-md p-5">
      <div className={`w-10 h-10 rounded-md border flex items-center justify-center ${tones[tone]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="mt-4 text-xs text-slate-500 uppercase tracking-wide font-semibold">{label}</div>
      <div className="mt-1 font-display text-2xl font-bold tabular text-white">{value}</div>
    </div>
  );
}

export default function AdminWhatsApp() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    // Reuse existing summary endpoint via activity API; for now we'll lightly poll a custom endpoint
    api.get("/admin/whatsapp/summary").then((r) => setStats(r.data)).catch(() => setStats({
      total_sent: 0, total_failed: 0, sent_today: 0, by_shop: [],
    }));
  }, []);

  if (!stats) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {["a","b","c","d"].map((slot) => (
          <div key={slot} className="h-32 bg-[#151A23] border border-slate-800 rounded-md animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-whatsapp-page">
      <div>
        <h1 className="font-display text-3xl font-bold text-white">WhatsApp Control</h1>
        <p className="text-slate-400 mt-1">Platform-wide messaging activity (simulated).</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Send} label="Sent (all-time)" value={stats.total_sent} tone="emerald" testid="wa-stat-sent" />
        <Stat icon={MessageCircle} label="Sent today" value={stats.sent_today} tone="sky" testid="wa-stat-today" />
        <Stat icon={AlertTriangle} label="Failed" value={stats.total_failed} tone="red" testid="wa-stat-failed" />
        <Stat icon={MessageCircle} label="Active shops" value={stats.by_shop?.length || 0} tone="slate" testid="wa-stat-shops" />
      </div>

      <div className="bg-[#151A23] border border-slate-800 rounded-md">
        <div className="p-4 border-b border-slate-800 font-display font-semibold text-white">Per-shop activity</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-900 text-slate-400">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Shop</th>
                <th className="text-right px-4 py-3 font-semibold">Sent</th>
                <th className="text-right px-4 py-3 font-semibold">Failed</th>
              </tr>
            </thead>
            <tbody>
              {(stats.by_shop || []).length === 0 && <tr><td colSpan={3} className="px-4 py-12 text-center text-slate-500">No WhatsApp activity yet.</td></tr>}
              {(stats.by_shop || []).map((r) => (
                <tr key={r.shop_id} className="border-t border-slate-800" data-testid={`wa-shop-${r.shop_id}`}>
                  <td className="px-4 py-3 text-white">{r.shop_name || r.shop_id?.slice(0, 8)}</td>
                  <td className="px-4 py-3 text-right tabular text-emerald-300">{r.sent}</td>
                  <td className="px-4 py-3 text-right tabular text-red-300">{r.failed}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
